import { useCallback, useState } from 'react';
import { useMediaQuery } from '@chakra-ui/react';
import {
    MOBILE_QUERY,
    SIDEBAR_COLLAPSED_KEY,
    SIDEBAR_EXPAND_QUERY,
} from '../layoutTokens';

export interface SidebarState {
    /** < 768px — no sidebar at all; a top bar and a bottom tab bar instead. */
    isMobile: boolean;
    /** 768–1179px, or a manual collapse at any width. */
    collapsed: boolean;
    /** Whether the manual toggle should be offered (there is no column to toggle on mobile). */
    canToggle: boolean;
    toggleCollapsed: () => void;
}

const readStoredCollapse = (): boolean | null => {
    try {
        const raw = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
        return raw === null ? null : raw === 'true';
    } catch {
        return null;
    }
};

/**
 * Sidebar sizing. Auto-rails below 1180px; a manual toggle overrides that and
 * persists per user.
 *
 * The drawer state that used to live here is gone: below 768px the design has no
 * sidebar to open, so there is nothing for a hamburger to reveal.
 */
export function useSidebarState(): SidebarState {
    const [canExpand] = useMediaQuery([SIDEBAR_EXPAND_QUERY], { fallback: [true] });
    const [isMobile] = useMediaQuery([MOBILE_QUERY], { fallback: [false] });

    const [manualCollapse, setManualCollapse] = useState<boolean | null>(readStoredCollapse);

    const collapsed = isMobile ? false : (manualCollapse ?? !canExpand);

    const toggleCollapsed = useCallback(() => {
        setManualCollapse((previous) => {
            const next = !(previous ?? !canExpand);
            try {
                localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
            } catch {
                /* storage unavailable — the toggle still works for this session */
            }
            return next;
        });
    }, [canExpand]);

    return {
        isMobile,
        collapsed,
        canToggle: !isMobile,
        toggleCollapsed,
    };
}
